/**
 * Sparkline: a small unlabelled trend line for a tile, drawn from plain data
 * (ADR-026) on Recharts. One accessible name for the whole line, no axes, the
 * last value as the peak marker.
 *
 * Author: Mahmoud Ragab Ahmed.
 */

import { Line, LineChart as RechartsLineChart, YAxis } from "recharts";

import { strings } from "../brand/strings";

import { ChartEmpty, SPARK_SIZE, surfaceClass } from "./shared";

interface SparkDotProps {
  readonly cx?: number;
  readonly cy?: number;
  readonly index?: number;
  readonly payload?: Record<string, unknown>;
  /** Closed in from the chart when the dot element is rendered. */
  readonly markerLabel?: string;
}

/** The last value carries the peak marker; the other points stay unadorned. */
function SparkDot(props: SparkDotProps): React.JSX.Element | null {
  const { cx, cy, payload, markerLabel } = props;
  if (cx === undefined || cy === undefined || payload === undefined) {
    return null;
  }
  const raw = payload.v;
  const value = typeof raw === "number" ? raw : Number(raw);
  const name =
    markerLabel === undefined || Number.isNaN(value)
      ? undefined
      : strings.charts.mark(markerLabel, value);
  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill="var(--q-chart-1)"
      stroke="var(--q-panel)"
      role="img"
      aria-label={name}
    >
      {name === undefined ? null : <title>{name}</title>}
    </circle>
  );
}

export function Sparkline({
  ariaLabel,
  values,
}: {
  ariaLabel: string;
  values: readonly number[];
}): React.JSX.Element {
  const total = values.reduce((sum, value) => sum + value, 0);
  if (values.length < 2 || total === 0) {
    return <ChartEmpty />;
  }
  const rows = values.map((value) => ({ v: value }));

  return (
    <div className="q-chart__frame q-chart__frame--tile" data-testid="sparkline">
      <RechartsLineChart
        width={SPARK_SIZE.width}
        height={SPARK_SIZE.height}
        data={rows}
        margin={{ top: 3, right: 3, bottom: 3, left: 3 }}
        role="img"
        aria-label={ariaLabel}
        ref={surfaceClass}
      >
        <YAxis hide domain={[0, "auto"]} />
        <Line
          type="linear"
          dataKey="v"
          stroke="var(--q-chart-1)"
          strokeWidth={2}
          dot={<SparkDot markerLabel={ariaLabel} />}
          activeDot={false}
          isAnimationActive={false}
        />
      </RechartsLineChart>
    </div>
  );
}
